/**
 * D1 — Visibilidade administrativa do beta.
 *
 * Tela única (admin-only) com a lista de TODOS os usuários cadastrados
 * + sinais de atividade. Reusa `AdminRoute` do App.js (que checa
 * `user.is_admin` do `/api/auth/me`) e o gate backend `require_admin`
 * em `GET /api/admin/users`.
 *
 * Sem paginação, sem filtros server-side, sem export — V1 minimalista
 * pra ~10–100 users do beta. Busca e ordenação são CLIENT-SIDE:
 * suficientes pra esse volume, sem ida ao backend.
 *
 * Cabeçalho mostra 2 totais derivados client-side a partir do mesmo
 * payload: cadastros totais e ativos últimos 7d (last_active_at >= 7d).
 */
import { useEffect, useMemo, useState } from "react";
import {
  ArrowUpDown, Search, Users as UsersIcon, Activity, AlertCircle,
  Loader2,
} from "lucide-react";
import { authFetch } from "@/lib/authFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const COLUMNS = [
  { id: "name",            label: "Nome",         sortable: true },
  { id: "email",           label: "E-mail",       sortable: true },
  { id: "plan",            label: "Plano",        sortable: true },
  { id: "created_at",      label: "Cadastrado",   sortable: true },
  { id: "last_active_at",  label: "Último uso",   sortable: true },
  { id: "n_actions_30d",   label: "Ações (30d)",  sortable: true, numeric: true },
];

const PLAN_LABEL = {
  free: "Free",
  essencial: "Essencial",
  essencial_fallback: "Essencial",
  pro: "Pro",
  pro_trial: "Trial",
  premium: "Premium",
};


function _fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "2-digit",
    });
  } catch { return "—"; }
}


function _fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const diff = Date.now() - d.getTime();
    const days = Math.floor(diff / 86_400_000);
    if (days < 1) return "hoje";
    if (days === 1) return "ontem";
    if (days < 7) return `${days}d atrás`;
    if (days < 30) return `${Math.floor(days / 7)}sem atrás`;
    if (days < 365) return `${Math.floor(days / 30)}m atrás`;
    return `${Math.floor(days / 365)}a atrás`;
  } catch { return "—"; }
}


function _activeLast7d(users) {
  const cutoff = Date.now() - 7 * 86_400_000;
  return users.filter((u) => {
    if (!u.last_active_at) return false;
    const t = new Date(u.last_active_at).getTime();
    return !isNaN(t) && t >= cutoff;
  }).length;
}


function _compare(a, b, key, numeric) {
  const va = a?.[key];
  const vb = b?.[key];
  // null/undefined no fim
  if (va == null && vb == null) return 0;
  if (va == null) return 1;
  if (vb == null) return -1;
  if (numeric) return Number(va) - Number(vb);
  // Strings (datas ISO comparam bem como string)
  return String(va).localeCompare(String(vb), "pt-BR");
}


export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  // ── Fetch ────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await authFetch(`${API_URL}/api/admin/users`);
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }
        const data = await r.json();
        if (!cancelled) setUsers(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Falha ao carregar usuários.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Filtro client-side (busca por nome ou email) ─────────────────
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    ));
  }, [users, query]);

  // ── Ordenação client-side ────────────────────────────────────────
  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.id === sortKey);
    const numeric = !!col?.numeric;
    const out = [...filtered].sort((a, b) => _compare(a, b, sortKey, numeric));
    return sortDir === "desc" ? out.reverse() : out;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (id) => {
    if (id === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(id); setSortDir("desc"); }
  };

  const total = users.length;
  const active7d = useMemo(() => _activeLast7d(users), [users]);

  return (
    <div
      className="min-h-screen bg-surface-canvas text-foreground"
      data-testid="admin-users-page"
    >
      <header className="border-b border-subtle">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center gap-3">
          <UsersIcon className="w-5 h-5 text-brand-cyan" aria-hidden="true" />
          <h1 className="font-display text-h2 text-foreground">
            Beta · Usuários
          </h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Cabeçalho com totais */}
        <div className="grid grid-cols-2 gap-4 mb-6" data-testid="admin-users-totals">
          <div className="glass-card rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-cyan/10 flex items-center justify-center">
              <UsersIcon className="w-5 h-5 text-brand-cyan" />
            </div>
            <div>
              <p className="text-micro text-foreground-subtle uppercase tracking-wider">
                Cadastros
              </p>
              <p
                className="text-h2 font-bold tabular-nums text-foreground"
                data-testid="admin-users-total"
              >
                {total}
              </p>
            </div>
          </div>
          <div className="glass-card rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-green/10 flex items-center justify-center">
              <Activity className="w-5 h-5 text-brand-green" />
            </div>
            <div>
              <p className="text-micro text-foreground-subtle uppercase tracking-wider">
                Ativos 7 dias
              </p>
              <p
                className="text-h2 font-bold tabular-nums text-foreground"
                data-testid="admin-users-active-7d"
              >
                {active7d}
              </p>
            </div>
          </div>
        </div>

        {/* Busca */}
        <div className="relative mb-4">
          <Search
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground-subtle"
            aria-hidden="true"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filtrar por nome ou e-mail…"
            data-testid="admin-users-search"
            className="w-full pl-10 pr-4 py-2.5 bg-surface-card border border-subtle rounded-xl text-foreground placeholder-foreground-subtle focus:outline-none focus:border-brand-cyan/40 transition-colors"
          />
        </div>

        {/* Tabela */}
        {loading ? (
          <div
            className="flex items-center justify-center py-16 text-foreground-subtle"
            data-testid="admin-users-loading"
          >
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Carregando usuários…
          </div>
        ) : error ? (
          <div
            data-testid="admin-users-error"
            className="flex items-start gap-2 px-4 py-3 rounded-lg bg-status-error/10 border border-status-error/20 text-status-error text-sm"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Não consegui carregar a lista. Tenta atualizar a página.</span>
          </div>
        ) : users.length === 0 ? (
          <div
            data-testid="admin-users-empty"
            className="text-center py-16 text-foreground-subtle"
          >
            Nenhum usuário ainda.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-subtle">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-surface-card-hover">
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.id}
                      onClick={() => c.sortable && toggleSort(c.id)}
                      className={`px-4 py-3 text-left text-micro font-medium text-foreground-subtle uppercase tracking-wider ${c.sortable ? "cursor-pointer hover:text-foreground" : ""}`}
                      data-testid={`admin-users-th-${c.id}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {c.sortable && (
                          <ArrowUpDown
                            className={`w-3 h-3 ${sortKey === c.id ? "text-brand-cyan" : "opacity-50"}`}
                            aria-hidden="true"
                          />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr>
                    <td
                      colSpan={COLUMNS.length}
                      className="px-4 py-10 text-center text-foreground-subtle"
                      data-testid="admin-users-empty-search"
                    >
                      Ninguém com esse nome ou e-mail.
                    </td>
                  </tr>
                ) : (
                  sorted.map((u) => (
                    <tr
                      key={u.id}
                      className="border-t border-subtle hover:bg-white/[0.02] transition-colors"
                      data-testid={`admin-users-row-${u.id}`}
                    >
                      <td className="px-4 py-3 text-foreground font-medium">{u.name || "—"}</td>
                      <td className="px-4 py-3 text-foreground-muted">{u.email || "—"}</td>
                      <td className="px-4 py-3 text-foreground-muted">
                        {PLAN_LABEL[u.plan] || u.plan || "—"}
                      </td>
                      <td className="px-4 py-3 text-foreground-muted" title={u.created_at || ""}>
                        {_fmtDate(u.created_at)}
                      </td>
                      <td className="px-4 py-3 text-foreground-muted" title={u.last_active_at || ""}>
                        {_fmtRelative(u.last_active_at)}
                      </td>
                      <td className="px-4 py-3 text-foreground tabular-nums">
                        {u.n_actions_30d ?? 0}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
